import sortBy from 'lodash/sortBy';
import {
  ActionPlanImpactTrendsChartAnnotations,
  ActionPlanImpactTrendsChartsColors,
  LineChartSeriesType,
} from './types';

export const getPointAnnotations = ({
  annotations,
  colorMap,
  handleActionSelectionInChart,
  setHighlightCurrentAction,
  actionIdToActionTypeIconMap,
  trendStartYear,
}: {
  annotations: ActionPlanImpactTrendsChartAnnotations;
  colorMap: ActionPlanImpactTrendsChartsColors;
  handleActionSelectionInChart?: (actionId: number) => void;
  setHighlightCurrentAction?: (actionId: number | null) => void;
  actionIdToActionTypeIconMap?: Record<number, string | undefined>;
  trendStartYear: number;
}): NonNullable<LineChartSeriesType['annotations']>['points'] => {
  const strandingAnnotationYears = annotations.reduce((acc: number[], annotation) => {
    if (
      annotation.annotation_type === 'BEFORE_ACTIONS_STRANDING_YEAR' ||
      annotation.annotation_type === 'AFTER_ACTIONS_STRANDING_YEAR'
    )
      return acc.concat([annotation.year]);
    return acc;
  }, []);
  const sortedAnnotations = sortBy(annotations, ['annotation_type', 'year', 'action_id'], ['asc', 'asc', 'asc']);
  let relativeAnnotationIndex = 0;

  return sortedAnnotations.map((annotation, index) => {
    switch (annotation.annotation_type) {
      case 'ACTION':
        const isAnnotationInStrandingYear = strandingAnnotationYears.includes(annotation.year);
        const isAnnotationInSameYearAsPreviousAnnotation = sortedAnnotations.at(index - 1)?.year === annotation.year;

        relativeAnnotationIndex = (() => {
          // actions happening in the same year and not overlapping with any stranding year
          if (isAnnotationInSameYearAsPreviousAnnotation && !isAnnotationInStrandingYear) {
            return relativeAnnotationIndex + 1;
          }
          // actions happening in the same year or overlapping with a stranding year
          if (isAnnotationInSameYearAsPreviousAnnotation || isAnnotationInStrandingYear) {
            return relativeAnnotationIndex + 1;
          }
          // actions happening in a different year
          return 0;
        })();

        const actionTypeIcon =
          annotation.action_id && actionIdToActionTypeIconMap
            ? actionIdToActionTypeIconMap[annotation.action_id]
            : undefined;

        const offsetY = relativeAnnotationIndex * -25;

        const imageMarker =
          actionTypeIcon !== undefined
            ? {
                marker: {
                  offsetY,
                  size: 15,
                },
                image: {
                  offsetY,
                  width: 20,
                  path: actionTypeIcon,
                },
              }
            : undefined;

        const labelMarker =
          actionTypeIcon === undefined && handleActionSelectionInChart === undefined
            ? {
                marker: {
                  offsetY: offsetY < 0 ? offsetY + relativeAnnotationIndex * 7 : 0,
                  size: 10,
                  fillColor: colorMap.black,
                  strokeColor: colorMap.black,
                },
                label: {
                  text: `${index + 1}`,
                  offsetY: 24 - relativeAnnotationIndex * 19,
                  borderRadius: 2,
                  borderColor: 'transparent',
                  style: {
                    background: 'transparent',
                    color: colorMap.white,
                    fontSize: '14px',
                    fontWeight: 600,
                  },
                },
              }
            : undefined;

        const clientHandlers =
          handleActionSelectionInChart && setHighlightCurrentAction
            ? {
                click: function (e: any) {
                  handleActionSelectionInChart(e.id);
                },
                mouseEnter: function (e: any) {
                  setHighlightCurrentAction(e.id);
                },
                mouseLeave: function () {
                  setHighlightCurrentAction(null);
                },
              }
            : undefined;

        return {
          x: annotation.year,
          y: annotation.value,
          id: annotation.action_id ?? undefined,
          ...imageMarker,
          ...clientHandlers,
          ...labelMarker,
        };
      case 'BEFORE_ACTIONS_STRANDING_YEAR':
        return {
          x: annotation.year,
          y: annotation.value,
          marker: {
            size: 6,
            strokeWidth: 8,
            fillColor: colorMap.strandingPath,
            offsetX: 0,
            strokeColor: '',
          },
        };
      default:
        return {
          x: annotation.year,
          y: annotation.value,
          marker: {
            size: 8,
            strokeColor: colorMap.strandingPathLight,
            strokeWidth: 6,
            fillColor: colorMap.strandingPath,
            offsetX: annotation.year === trendStartYear ? 5 : 0,
          },
        };
    }
  });
};
